import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { GqlPermissions } from "~/__generated__";
import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaPageComponent from "~/modules/app/components/GaiaPageComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import useClientOrProjectScope from "~/modules/app/hooks/useClientOrProjectScope";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import PossibleVisitsGraph from "../../components/PossibleVisitsGraph";
import ProtocolCourseList from "../../components/ProtocolCourseList";
import ProtocolForm, { ProtocolFormValues } from "../../components/ProtocolForm";
import ProtocolVisitsEditor from "../../components/ProtocolVisitsEditor";
import useProtocolEditRoute_Delete from "./api/useProtocolEditRoute_Delete";
import useProtocolEditRoute_GetById from "./api/useProtocolEditRoute_GetById";
import useProtocolEditRoute_Update from "./api/useProtocolEditRoute_Update";

export default function ProtocolEditRoute() {
    const { t } = useTranslation("protocol");
    const { protocolId } = useParams();
    const navigate = useNavigate();
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    if (!protocolId) throw Error("No protocolId param in url");

    const { data, loading, error } = useProtocolEditRoute_GetById(protocolId);
    const { updateProtocol, error: updateError } = useProtocolEditRoute_Update();
    const { deleteProtocol, loading: deleteLoading } = useProtocolEditRoute_Delete();

    const editScope = useClientOrProjectScope();
    const isProjectScope = editScope.project && !editScope.client;

    const deleteButtonLabel = isProjectScope
        ? t("protocolDeleteButton.projectScopeBtnDelete")
        : t("protocolDeleteButton.btnDelete");

    const title = isProjectScope
        ? isAdmin
            ? t("protocolEditScreen.projectScopeTitle")
            : t("protocolEditScreen.projectScopeTitleNonAdmin")
        : isAdmin
          ? t("protocolEditScreen.title")
          : t("protocolEditScreen.titleNonAdmin");

    const handleSubmit = async (data: ProtocolFormValues) => {
        const result = await updateProtocol(protocolId, {
            name: data.name,
            description: data.description,
            researchTypeId: data.researchTypeId,
            collectAllMediaPoints: data.collectAllMediaPoints === "collect-all",
            requiresRouteShuffling: data.requiresRouteShuffling,
        });

        if (result.data?.protocolUpdate) {
            navigate("..", { replace: true });
        }
    };

    const handleDelete = async () => {
        await deleteProtocol(protocolId);
        navigate("..", { replace: true });
    };

    if (error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn>
            <GaiaPageComponent title={title} isLoading={loading}>
                {data && (
                    <ProtocolForm
                        protocolData={{
                            name: data.name,
                            description: data.description,
                            researchTypeId: data.researchTypeId,
                            requiresRouteShuffling: data.requiresRouteShuffling,
                            collectAllMediaPoints: data.collectAllMediaPoints ? "collect-all" : "collect-observations-only",
                        }}
                        error={updateError}
                        onSubmit={handleSubmit}
                    />
                )}
            </GaiaPageComponent>
            <GaiaPageComponent title={t("protocolEditScreen.visitsTitle")} titleVariant="h3" isLoading={loading}>
                {data && <PossibleVisitsGraph protocols={[data]} />}
                {data?.visits && <ProtocolVisitsEditor protocol={data} visits={data.visits} />}
            </GaiaPageComponent>

            <GaiaPageComponent title={t("protocolEditScreen.researchRequirementsTitle")} titleVariant="h3" isLoading={loading}>
                <ProtocolCourseList data={data?.courses || []} />
            </GaiaPageComponent>

            {isAdmin && (
                <GaiaPageComponent title={t("protocolEditScreen.dangertitle")} titleVariant="h3">
                    {data && <GaiaDeleteButton label={deleteButtonLabel} isLoading={deleteLoading} onDelete={handleDelete} />}
                </GaiaPageComponent>
            )}
        </GaiaPageLayoutColumn>
    );
}
